import React, { useEffect, useRef, useState } from "react";
import { heroBadges, heroSlides } from "../../App/Landing/landingContent";
import prefersReducedMotion from "../../utility/prefersReducedMotion";
import { Icon } from "./Icon";
import Reveal from "./Reveal";

export default function Hero() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const parallaxRef = useRef<HTMLDivElement>(null);
  const count = heroSlides.length;

  const go = (n: number) => setIndex((n + count) % count);

  useEffect(() => {
    if (paused || prefersReducedMotion()) return;
    const timer = setInterval(() => setIndex((i) => (i + 1) % count), 5000);
    return () => clearInterval(timer);
  }, [paused, count]);

  useEffect(() => {
    if (prefersReducedMotion()) return;
    let ticking = false;
    const update = () => {
      const y = window.scrollY;
      if (parallaxRef.current && y < 900) {
        parallaxRef.current.style.transform = `translate3d(0,${Math.min(y * 0.06, 22)}px,0)`;
      }
      ticking = false;
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    update();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current !== null) {
      const dx = e.changedTouches[0].clientX - touchStartX.current;
      if (Math.abs(dx) > 40) go(index + (dx < 0 ? 1 : -1));
    }
    touchStartX.current = null;
    setPaused(false);
  };

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-white via-brand-mist to-brand-soft">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-center py-12 lg:py-0 lg:min-h-[300px]">
        <div className="relative z-10 lg:py-14">
          <Reveal as="h1" delay={0} className="display mt-3 text-4xl sm:text-5xl lg:text-[3.4rem]">
            High-Quality Prints<br />for <span className="font-normal">Every Big Idea.</span>
          </Reveal>
          <Reveal as="p" delay={90} className="mt-5 max-w-md text-base text-slate-600 leading-relaxed">
            Custom banners, yard signs and foam core prints at budget-friendly prices. Perfect for businesses, events, promotions and more.
          </Reveal>
          <Reveal delay={180} className="mt-7 flex flex-wrap gap-3">
            <a href="#products" className="inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-brand-dark">
              Shop Print Products <Icon name="arrow" className="h-4 w-4" />
            </a>
          </Reveal>
          <Reveal as="ul" delay={270} className="mt-8 flex flex-wrap gap-x-6 gap-y-3 text-xs text-slate-600">
            {heroBadges.map((b) => (
              <li key={b.label} className="flex items-center gap-2">
                <Icon name={b.icon} className="h-7 w-7 text-brand" />
                {b.label}
              </li>
            ))}
          </Reveal>
        </div>

        <Reveal
          anim="zoom"
          delay={200}
          className="order-first lg:order-none relative h-[260px] sm:h-[340px] lg:h-full lg:min-h-[340px] overflow-hidden rounded-2xl lg:rounded-none lg:-mr-8 xl:-mr-[calc((100vw-80rem)/2+2rem)]"
          role="region"
          aria-roledescription="carousel"
          aria-label="Featured prints"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocus={() => setPaused(true)}
          onBlur={() => setPaused(false)}
          onTouchStart={(e: React.TouchEvent) => {
            touchStartX.current = e.touches[0].clientX;
            setPaused(true);
          }}
          onTouchEnd={onTouchEnd}
        >
          <div
            ref={parallaxRef}
            className="absolute inset-x-0 -top-[10%] -bottom-[10%] will-change-transform lg:[mask-image:linear-gradient(to_right,transparent,#000_18%)]"
          >
            {heroSlides.map((s, k) => (
              <div
                key={s.src}
                className={`absolute inset-0 transition-opacity duration-700 ${k === index ? "opacity-100" : "opacity-0"}`}
                aria-hidden={k !== index}
              >
                <img
                  src={s.src}
                  alt={s.alt}
                  width={1200}
                  height={760}
                  loading={k === 0 ? "eager" : "lazy"}
                  className="h-full w-full object-cover object-right"
                />
              </div>
            ))}
          </div>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-3 rounded-full bg-white/90 px-3 py-2 shadow-md backdrop-blur">
            <button type="button" onClick={() => go(index - 1)} className="flex h-6 w-6 items-center justify-center rounded-full text-ink hover:bg-brand-soft" aria-label="Previous slide">
              <Icon name="arrow" className="h-4 w-4 rotate-180" />
            </button>
            <div className="flex items-center gap-1.5">
              {heroSlides.map((s, k) => (
                <button
                  key={s.src}
                  type="button"
                  onClick={() => go(k)}
                  className={`h-2 rounded-full transition-all ${k === index ? "w-6 bg-brand" : "w-2 bg-slate-300"}`}
                  aria-label={`Show slide ${k + 1}`}
                  aria-current={k === index ? "true" : undefined}
                />
              ))}
            </div>
            <button type="button" onClick={() => go(index + 1)} className="flex h-6 w-6 items-center justify-center rounded-full text-ink hover:bg-brand-soft" aria-label="Next slide">
              <Icon name="arrow" className="h-4 w-4" />
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
